/**
 * Cache invalidation event system for cross-component communication
 */

import { devLog } from '@/utils/devLog';

type CacheEventType = 'projects-invalidated' | 'users-invalidated' | 'stats-invalidated' | 'organizations-invalidated';

interface CacheEvent {
  type: CacheEventType;
  tags: string[];
  timestamp: number;
}

class CacheEventEmitter extends EventTarget {
  emit(type: CacheEventType, tags: string[]) {
    const event = new CustomEvent<CacheEvent>('cache-invalidation', {
      detail: {
        type,
        tags,
        timestamp: Date.now()
      }
    });
    this.dispatchEvent(event);
  }

  on(callback: (event: CustomEvent<CacheEvent>) => void) {
    this.addEventListener('cache-invalidation', callback as EventListener);
  }

  off(callback: (event: CustomEvent<CacheEvent>) => void) {
    this.removeEventListener('cache-invalidation', callback as EventListener);
  }
}

export const cacheEvents = new CacheEventEmitter();

/**
 * Hook to listen for cache invalidation events
 */
import { useEffect } from 'react';

export function useCacheInvalidation(tags: string[], callback: () => void) {
  useEffect(() => {
    const handleCacheInvalidation = (event: CustomEvent<CacheEvent>) => {
      const eventTags = event.detail.tags;
      const shouldInvalidate = tags.some(tag => eventTags.includes(tag));
      
      if (shouldInvalidate) {
        devLog(`Cache invalidation detected for tags: ${eventTags.join(', ')}`);
        callback();
      }
    };

    cacheEvents.on(handleCacheInvalidation);
    
    return () => {
      cacheEvents.off(handleCacheInvalidation);
    };
  }, [tags, callback]);
}